import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

// Calcolo Ticket per-stanza (indipendente da giornate e da Survival).
// Mostra le partite della stanza, l'admin inserisce i risultati e liquida.
export default function CalcolaStanza({ roomId, settled, onSettled }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/rooms/${roomId}/settle-preview`);
      setRows((r.matches || []).map((m) => ({
        home_team: m.home_team, away_team: m.away_team, date: m.date, time: m.time,
        home: m.home_score != null ? String(m.home_score) : "",
        away: m.away_score != null ? String(m.away_score) : "",
        postponed: !!m.postponed, suspended: !!m.suspended, pending: !!m.pending,
      })));
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [roomId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const setField = (i, k, v) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const togglePost = (i) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, postponed: !row.postponed, suspended: false, pending: false } : row));
  const toggleSus = (i) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, suspended: !row.suspended, postponed: false, pending: false } : row));
  const togglePending = (i) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, pending: !row.pending, postponed: false, suspended: false } : row));

  const anyPending = rows.some((row) => row.pending);

  const settle = async () => {
    const fixtures = [];
    for (const row of rows) {
      if (row.pending) {
        fixtures.push({ home_team: row.home_team, away_team: row.away_team, home_score: 0, away_score: 0, pending: true });
      } else if (row.postponed) {
        fixtures.push({ home_team: row.home_team, away_team: row.away_team, home_score: 0, away_score: 0, postponed: true });
      } else {
        if (row.home === "" || row.away === "") { toast.error(`Inserisci il risultato di ${row.home_team}-${row.away_team} (o segnala "In attesa")`); return; }
        fixtures.push({
          home_team: row.home_team, away_team: row.away_team,
          home_score: Number(row.home), away_score: Number(row.away),
          suspended: !!row.suspended,
        });
      }
    }
    if (fixtures.length === 0) { toast.error("Nessuna partita da calcolare"); return; }
    const msg = anyPending
      ? "Ci sono partite IN ATTESA (quota 1.00): faccio un calcolo PARZIALE. Classifica e premi si aggiornano ma la stanza NON viene chiusa. Procedo?"
      : "Calcolare i risultati e CHIUDERE questa stanza (premi definitivi)?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await api(`/rooms/${roomId}/settle`, { method: "POST", body: { fixtures } });
      toast.success(r.partial ? "Calcolo parziale aggiornato (stanza ancora aperta)" : "Stanza calcolata e chiusa! Classifica e premi definitivi.");
      onSettled?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const reopen = async () => {
    if (!window.confirm("Riaprire la stanza? I risultati verranno rimossi e i premi azzerati.")) return;
    setBusy(true);
    try {
      await api(`/rooms/${roomId}/reopen`, { method: "POST" });
      toast.success("Stanza riaperta");
      onSettled?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button data-testid="calc-stanza-open" onClick={() => setOpen(true)} className="w-full flex items-center justify-center gap-2 border border-[#10B981]/40 bg-[#10B981]/5 text-[#10B981] rounded-xl py-2.5 text-sm font-bold hover:bg-[#10B981]/10">
        <Calculator size={16} /> {settled ? "Ricalcola stanza (admin)" : "Calcola stanza (admin)"}
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-[#10B981]/40 bg-[#181D22] p-4 space-y-3" data-testid="calc-stanza-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-extrabold flex items-center gap-2"><Calculator size={16} className="text-[#10B981]" /> Calcolo risultati stanza</div>
        <div className="flex items-center gap-3">
          {settled && <button data-testid="calc-stanza-reopen" onClick={reopen} disabled={busy} className="inline-flex items-center gap-1 text-xs text-[#F59E0B] hover:underline"><RotateCcw size={12} /> Riapri</button>}
          <button data-testid="calc-stanza-close" onClick={() => setOpen(false)} className="text-xs text-[#94A3B8] hover:text-white">Chiudi</button>
        </div>
      </div>
      <p className="text-xs text-[#64748B]">Inserisci i risultati. <b className="text-[#93C5FD]">In attesa</b> = partita non ancora giocata (quota 1.00, la stanza resta aperta). Rinviata = evento neutro (quota 1.00); Sospesa = 1X2 nullo, gli altri mercati valutati secondo il betting.</p>

      {loading ? (
        <div className="py-6 text-center text-[#94A3B8] text-sm">Caricamento partite...</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-[#94A3B8] text-sm">Nessuna partita trovata. Imposta prima la griglia quote o attendi le prime giocate.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => {
            const cls = row.pending ? "border-[#3B82F6]/50 bg-[#3B82F6]/5" : row.postponed ? "border-[#EF4444]/40 bg-[#EF4444]/5" : row.suspended ? "border-[#F59E0B]/40 bg-[#F59E0B]/5" : "border-white/10 bg-[#0F1216]";
            return (
              <div key={i} data-testid={`calc-fx-${i}`} className={`rounded-lg border p-2.5 ${cls}`}>
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="flex-1 min-w-[130px] truncate">{row.home_team} <span className="text-[#94A3B8]">vs</span> {row.away_team}
                    {row.pending && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#3B82F6]/20 text-[#93C5FD] font-bold">IN ATTESA</span>}
                  </span>
                  <input data-testid={`calc-h-${i}`} type="number" min="0" disabled={row.postponed || row.pending} value={row.home} onChange={(e) => setField(i, "home", e.target.value)} className="w-12 bg-[#181D22] border border-white/15 rounded px-2 py-1 text-center disabled:opacity-40" />
                  <span className="text-[#94A3B8]">-</span>
                  <input data-testid={`calc-a-${i}`} type="number" min="0" disabled={row.postponed || row.pending} value={row.away} onChange={(e) => setField(i, "away", e.target.value)} className="w-12 bg-[#181D22] border border-white/15 rounded px-2 py-1 text-center disabled:opacity-40" />
                  <label className="flex items-center gap-1 text-xs text-[#93C5FD] cursor-pointer ml-1">
                    <input data-testid={`calc-pend-${i}`} type="checkbox" checked={row.pending} onChange={() => togglePending(i)} /> In attesa
                  </label>
                  <label className="flex items-center gap-1 text-xs text-[#94A3B8] cursor-pointer ml-1">
                    <input data-testid={`calc-post-${i}`} type="checkbox" checked={row.postponed} onChange={() => togglePost(i)} /> Rinviata
                  </label>
                  <label className="flex items-center gap-1 text-xs text-[#F59E0B] cursor-pointer ml-1">
                    <input data-testid={`calc-sus-${i}`} type="checkbox" checked={row.suspended} onChange={() => toggleSus(i)} /> Sospesa
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rows.length > 0 && (
        <button data-testid="calc-stanza-settle" onClick={settle} disabled={busy} className={`w-full font-bold rounded-md py-2.5 disabled:opacity-50 ${anyPending ? "bg-[#3B82F6] text-white" : "bg-[#10B981] text-[#04130D]"}`}>
          {busy ? "Calcolo..." : anyPending ? "Ricalcola (parziale, stanza aperta)" : (settled ? "Ricalcola e chiudi (premi definitivi)" : "Calcola e chiudi premi")}
        </button>
      )}
    </div>
  );
}
